import React, { useCallback, useState } from 'react';
import { LegendConfig, DatasetConfig, ChartDimensions } from '../../types';

interface LegendProps {
  config: LegendConfig;
  datasets: DatasetConfig[];
  colors: string[];
  dimensions: ChartDimensions;
  onToggleDataset?: (index: number) => void;
  isMobile?: boolean;
}

/**
 * Legend component - Displays dataset labels with color indicators
 * Fully accessible with keyboard navigation
 */
export const Legend: React.FC<LegendProps> = ({
  config,
  datasets,
  colors,
  dimensions,
  onToggleDataset,
  isMobile = false,
}) => {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  // All hooks must be called before any early returns
  const handleClick = useCallback((index: number) => {
    if (config.onClick) {
      config.onClick(index, datasets[index]);
    }
    onToggleDataset?.(index);
  }, [config, datasets, onToggleDataset]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent, index: number, itemsLength: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(index);
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIndex = (index + 1) % itemsLength;
      setFocusedIndex(nextIndex);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = (index - 1 + itemsLength) % itemsLength;
      setFocusedIndex(prevIndex);
    }
  }, [handleClick]);

  // Early return AFTER all hooks
  if (!config.display) return null;

  const {
    position = 'top',
    align = 'center',
    labels = {},
  } = config;

  const {
    boxWidth = isMobile ? 10 : 12,
    boxHeight = isMobile ? 10 : 12,
    padding = isMobile ? 12 : 20,
    fontSize = isMobile ? 10 : 12,
    fontFamily,
    color = '#666',
  } = labels;

  const itemGap = padding;
  const { chartWidth, chartHeight, padding: chartPadding } = dimensions;

  // Calculate legend items
  const items = datasets.map((dataset, index) => ({
    label: dataset.label,
    color: colors[index % colors.length],
    hidden: dataset.hidden,
    index,
  }));

  // Calculate positioning
  const isVertical = position === 'left' || position === 'right';
  
  // Calculate total width for horizontal legend
  const itemWidths = items.map(item => {
    const textWidth = item.label.length * fontSize * 0.55;
    return boxWidth + 6 + textWidth + itemGap;
  });
  const totalWidth = itemWidths.reduce((sum, w) => sum + w, 0) - itemGap;

  // For mobile, wrap legend if too wide
  const shouldWrap = isMobile && totalWidth > chartWidth;

  // Starting position based on alignment
  let startX = 0;
  if (!isVertical && !shouldWrap) {
    switch (align) {
      case 'start':
        startX = 0;
        break;
      case 'end':
        startX = chartWidth - totalWidth;
        break;
      case 'center':
      default:
        startX = (chartWidth - totalWidth) / 2;
    }
  }

  // Legend position offset from chart
  let legendY = 0;
  let legendX = 0;
  
  switch (position) {
    case 'top':
      legendY = -chartPadding.top + (isMobile ? 8 : 10);
      break;
    case 'bottom':
      // Position legend below the x-axis labels with adequate spacing
      legendY = chartHeight + chartPadding.bottom - (isMobile ? 15 : 20);
      break;
    case 'left':
      legendX = -chartPadding.left + 10;
      break;
    case 'right':
      legendX = chartWidth + 10;
      break;
  }

  const isInteractive = onToggleDataset || config.onClick;

  // Render horizontal legend (possibly wrapped for mobile)
  if (!isVertical) {
    let currentX = shouldWrap ? 0 : startX;
    let currentY = 0;
    const lineHeight = Math.max(boxHeight, fontSize) + 8;
    
    return (
      <g 
        className="legend" 
        transform={`translate(0, ${legendY})`}
        role="list"
        aria-label="Chart legend"
      >
        {items.map((item, i) => {
          const itemWidth = itemWidths[i];
          
          // Wrap to next line if needed
          if (shouldWrap && currentX + itemWidth > chartWidth && currentX > 0) {
            currentX = 0;
            currentY += lineHeight;
          }
          
          const x = currentX;
          const y = currentY;
          currentX += itemWidth;
          
          const isFocused = focusedIndex === i;
          
          return (
            <g
              key={i}
              transform={`translate(${x}, ${y})`}
              onClick={() => handleClick(i)}
              onKeyDown={(e) => handleKeyDown(e, i, items.length)}
              onFocus={() => setFocusedIndex(i)}
              onBlur={() => setFocusedIndex(null)}
              tabIndex={isInteractive ? 0 : -1}
              role="listitem"
              aria-label={`${item.label}${item.hidden ? ' (hidden)' : ''}`}
              aria-pressed={!item.hidden}
              style={{ 
                cursor: isInteractive ? 'pointer' : 'default',
                outline: 'none',
              }}
            >
              {/* Focus indicator */}
              {isFocused && (
                <rect
                  x={-4}
                  y={-4}
                  width={itemWidth - itemGap + 8}
                  height={boxHeight + 8}
                  fill="none"
                  stroke="#0066ff"
                  strokeWidth={2}
                  rx={4}
                  aria-hidden="true"
                />
              )}
              
              {/* Color box */}
              <rect
                x={0}
                y={0}
                width={boxWidth}
                height={boxHeight}
                fill={item.hidden ? '#ccc' : item.color}
                rx={2}
                aria-hidden="true"
                style={{
                  transition: 'fill 0.2s ease',
                }}
              />
              
              {/* Label text */}
              <text
                x={boxWidth + 6}
                y={boxHeight / 2}
                dominantBaseline="middle"
                fill={item.hidden ? '#999' : color}
                fontSize={fontSize}
                fontFamily={fontFamily}
                style={{ 
                  textDecoration: item.hidden ? 'line-through' : 'none',
                  transition: 'fill 0.2s ease',
                  userSelect: 'none',
                }}
                aria-hidden="true"
              >
                {item.label}
              </text>
            </g>
          );
        })}
      </g>
    );
  }

  // Render vertical legend
  const lineHeight = Math.max(boxHeight, fontSize) + 8;
  
  return (
    <g 
      className="legend" 
      transform={`translate(${legendX}, ${legendY})`}
      role="list"
      aria-label="Chart legend"
    >
      {items.map((item, i) => {
        const isFocused = focusedIndex === i;
        
        return (
          <g
            key={i}
            transform={`translate(0, ${i * lineHeight})`}
            onClick={() => handleClick(i)}
            onKeyDown={(e) => handleKeyDown(e, i, items.length)}
            onFocus={() => setFocusedIndex(i)}
            onBlur={() => setFocusedIndex(null)}
            tabIndex={isInteractive ? 0 : -1}
            role="listitem"
            aria-label={`${item.label}${item.hidden ? ' (hidden)' : ''}`}
            aria-pressed={!item.hidden}
            style={{ 
              cursor: isInteractive ? 'pointer' : 'default',
              outline: 'none',
            }}
          >
            {/* Focus indicator */}
            {isFocused && (
              <rect
                x={-4}
                y={-4}
                width={boxWidth + item.label.length * fontSize * 0.6 + 16}
                height={boxHeight + 8}
                fill="none"
                stroke="#0066ff"
                strokeWidth={2}
                rx={4}
                aria-hidden="true"
              />
            )}
            
            <rect
              x={0}
              y={0}
              width={boxWidth}
              height={boxHeight}
              fill={item.hidden ? '#ccc' : item.color}
              rx={2}
              aria-hidden="true"
            />
            <text
              x={boxWidth + 6}
              y={boxHeight / 2}
              dominantBaseline="middle"
              fill={item.hidden ? '#999' : color}
              fontSize={fontSize}
              fontFamily={fontFamily}
              style={{ 
                textDecoration: item.hidden ? 'line-through' : 'none',
                userSelect: 'none',
              }}
              aria-hidden="true"
            >
              {item.label}
            </text>
          </g>
        );
      })}
    </g>
  );
};

export default Legend;
